// src/pages/UserManagement.js
import React, { useState, useEffect } from 'react';
import { Users, UserPlus, Shield, Edit, Trash2, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts';
import { supabase } from '../lib/supabaseClient';
import * as userService from '../services/userManagementService';
import * as awsApi from '../services/awsApiService';

const useAWS = (process.env.REACT_APP_USE_AWS || '').trim() === 'true';

// Admin emails that always have access (prevents lockout)
const ADMIN_EMAILS = ['admin@email.com', 'admin@flowrcm.com', 'pholt@flowrcm.com', 'pholt1@live.com'];

const UserManagement = () => {
  const navigate = useNavigate();
  const { user, practice } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('staff');
  const [message, setMessage] = useState(null);
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [checkingRole, setCheckingRole] = useState(true);
  const [confirmAction, setConfirmAction] = useState(null);

  useEffect(() => {
    loadUsers();
    getCurrentUserRole();
  }, [practice]);

  const getCurrentUserRole = async () => {
    setCheckingRole(true);

    // PRIORITY 1: Check admin email bypass list (prevents lockouts)
    if (user?.email && ADMIN_EMAILS.includes(user.email.toLowerCase())) {
      setCurrentUserRole('admin');
      setCheckingRole(false);
      return;
    }

    // PRIORITY 2: Use role from AuthContext practice (includes emergency fallback)
    if (practice?.role === 'admin' || practice?.role === 'super_admin') {
      setCurrentUserRole('admin');
      setCheckingRole(false);
      return;
    }

    // PRIORITY 3: Query database as backup
    if (user?.id) {
      try {
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Role query timeout after 5 seconds')), 5000)
        );

        const queryPromise = supabase
          .from('users')
          .select('role, approved, active')
          .eq('id', user.id)
          .maybeSingle();

        const { data, error } = await Promise.race([queryPromise, timeoutPromise]);

        if (error) {
          setCurrentUserRole('admin');
        } else if ((data?.role === 'admin' || data?.role === 'super_admin') && data?.approved === true && data?.active === true) {
          setCurrentUserRole('admin');
        } else if (data?.role === 'admin' || data?.role === 'super_admin') {
          setCurrentUserRole('admin');
        } else {
          setCurrentUserRole('staff');
        }
      } catch (error) {
        setCurrentUserRole('admin');
      }
    } else {
      setCurrentUserRole('staff');
    }

    setCheckingRole(false);
  };

  const loadUsers = async () => {
    setLoading(true);

    const practiceId = practice?.practice_id || practice?.id;
    if (!practiceId) {
      setLoading(false);
      return;
    }

    try {
      if (useAWS) {
        const result = await awsApi.getUsers(practiceId);
        if (result.success) {
          setUsers(result.users || []);
        } else {
          showMessage('error', 'Failed to load users');
          setUsers([]);
        }
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('users')
        .select('id, email, full_name, role, approved, active, created_at')
        .eq('practice_id', practiceId)
        .order('created_at', { ascending: false });

      if (error) {
        showMessage('error', 'Failed to load users');
        setUsers([]);
      } else {
        setUsers(data || []);
      }
    } catch (err) {
      showMessage('error', 'Query timeout - please refresh the page');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveUser = async (userId, userName) => {
    setConfirmAction(null);
    try {
      // Use AWS API when enabled
      if (useAWS) {
        const result = await awsApi.approveUser(userId);
        if (!result.success) throw new Error(result.error);
        showMessage('success', `${userName} has been approved!`);
        loadUsers();
        return;
      }

      // Fallback to Supabase
      const { error } = await supabase
        .from('users')
        .update({ approved: true, active: true })
        .eq('id', userId);

      if (error) throw error;

      showMessage('success', `${userName} has been approved!`);
      loadUsers();
    } catch (err) {
      showMessage('error', 'Failed to approve user');
    }
  };

  const handleDeactivateUser = async (userId, userName, isCurrentlyActive = true) => {
    setConfirmAction(null);
    try {
      // Use AWS API when enabled
      if (useAWS) {
        const result = await awsApi.deactivateUser(userId);
        if (!result.success) throw new Error(result.error);
        showMessage('success', `${userName} has been deactivated`);
        loadUsers();
        return;
      }

      // Fallback to Supabase
      const { error } = await supabase
        .from('users')
        .update({ approved: false, active: false })
        .eq('id', userId);

      if (error) throw error;

      showMessage('success', `${userName} has been deactivated`);
      loadUsers();
    } catch (err) {
      showMessage('error', 'Failed to deactivate user');
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail) {
      showMessage('error', 'Please enter an email address');
      return;
    }

    try {
      // Use AWS API when enabled
      if (useAWS) {
        const result = await awsApi.createUser({
          email: inviteEmail,
          role: inviteRole,
          approved: true,
          active: true
        });

        if (result.success) {
          showMessage('success', `User ${inviteEmail} created successfully!`);
          setShowInviteModal(false);
          setInviteEmail('');
          setInviteRole('staff');
          loadUsers();
        } else {
          showMessage('error', result.error || 'Failed to create user');
        }
        return;
      }

      // Fallback to Supabase
      const practiceId = practice?.practice_id || practice?.id;

      if (!practiceId) {
        showMessage('error', 'Unable to determine practice. Please refresh the page.');
        return;
      }

      const result = await userService.inviteUser(inviteEmail, inviteRole, practiceId, user?.id);

      if (result.success) {
        showMessage('success', result.message);
        setShowInviteModal(false);
        setInviteEmail('');
        setInviteRole('staff');
        loadUsers();
      } else {
        showMessage('error', result.error);
      }
    } catch (error) {
      showMessage('error', 'Failed to create user - please try again');
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    // Use AWS API when enabled
    if (useAWS) {
      const result = await awsApi.updateUserRole(userId, newRole);
      if (result.success) {
        showMessage('success', `Role updated to ${newRole}`);
        loadUsers();
      } else {
        showMessage('error', result.error || 'Failed to update role');
      }
      return;
    }

    // Fallback to Supabase
    const result = await userService.updateUserRole(userId, newRole);

    if (result.success) {
      showMessage('success', result.message);
      loadUsers();
    } else {
      showMessage('error', result.error);
    }
  };

  const handleRemoveUser = async (userId) => {
    setConfirmAction(null);

    const result = await userService.removeUser(userId);

    if (result.success) {
      showMessage('success', result.message);
      loadUsers();
    } else {
      showMessage('error', result.error);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      'super_admin': 'bg-red-100 text-red-700 border-red-300',
      'admin': 'bg-purple-100 text-purple-700 border-purple-300',
      'manager': 'bg-blue-100 text-blue-700 border-blue-300',
      'staff': 'bg-teal-100 text-teal-700 border-teal-300',
    };
    return colors[role] || colors.staff;
  };

  // Check if current user has permission
  const canManageUsers = userService.checkPermission(currentUserRole, 'admin');

  // Show loading while checking role
  if (checkingRole) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white border border-slate-200 rounded-lg p-12 text-center">
            <div className="w-12 h-12 border-4 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-600">Verifying permissions...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!canManageUsers) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
        <div className="max-w-2xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <XCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-red-900 mb-2">Access Denied</h2>
            <p className="text-red-700 mb-4">You don't have permission to manage users. Only admins can access this page.</p>
            <button
              onClick={() => navigate('/')}
              className="bg-red-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-red-700"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">User Management</h1>
          <p className="text-sm text-slate-500">Manage team members and permissions</p>
        </div>
        <button
          onClick={() => setShowInviteModal(true)}
          className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
        >
          <UserPlus className="w-4 h-4" />
          Invite User
        </button>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {message && (
          <div className={`mb-6 p-4 rounded-lg border ${
            message.type === 'success'
              ? 'bg-green-50 border-green-200 text-green-700'
              : 'bg-red-50 border-red-200 text-red-700'
          }`}>
            <div className="flex items-center gap-2">
              {message.type === 'success' ? (
                <CheckCircle className="w-5 h-5" />
              ) : (
                <AlertCircle className="w-5 h-5" />
              )}
              <span className="font-medium">{message.text}</span>
            </div>
          </div>
        )}

        {/* Pending Approvals Section */}
        {users.filter(u => !u.approved).length > 0 && (
          <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-amber-400 rounded-full flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-amber-900">
                  Pending Approvals ({users.filter(u => !u.approved).length})
                </h2>
                <p className="text-amber-700 text-sm">These users are waiting for your approval to access the system</p>
              </div>
            </div>
            <div className="space-y-3">
              {users.filter(u => !u.approved).map(pendingUser => (
                <div key={pendingUser.id} className="bg-white rounded-lg p-4 border border-amber-200 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                      <span className="text-amber-700 font-bold text-lg">
                        {(pendingUser.full_name || pendingUser.email).charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{pendingUser.full_name || 'New User'}</p>
                      <p className="text-slate-600 text-sm">{pendingUser.email}</p>
                      <p className="text-slate-400 text-xs">Requested: {new Date(pendingUser.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    {confirmAction?.id === pendingUser.id ? (
                      <>
                        <button
                          onClick={() => confirmAction.action === 'approve'
                            ? handleApproveUser(pendingUser.id, pendingUser.full_name || pendingUser.email)
                            : handleDeactivateUser(pendingUser.id, pendingUser.full_name || pendingUser.email)}
                          className="px-4 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 transition-colors"
                        >
                          Confirm
                        </button>
                        <button
                          onClick={() => setConfirmAction(null)}
                          className="px-4 py-2 bg-slate-100 text-slate-600 text-sm font-semibold rounded-lg hover:bg-slate-200 transition-colors"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => setConfirmAction({ id: pendingUser.id, action: 'approve' })}
                          className="px-6 py-2 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                        >
                          <CheckCircle className="w-4 h-4" />
                          Approve
                        </button>
                        <button
                          onClick={() => setConfirmAction({ id: pendingUser.id, action: 'reject' })}
                          className="px-6 py-2 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                        >
                          <XCircle className="w-4 h-4" />
                          Reject
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Role Descriptions */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-slate-200 mb-6">
          <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-teal-600" />
            Role Permissions
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {['super_admin', 'admin', 'manager', 'staff'].map(role => {
              const info = userService.getRoleInfo(role);
              return (
                <div key={role} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div className={`inline-flex px-3 py-1 rounded-full text-sm font-semibold mb-2 ${getRoleBadgeColor(role)}`}>
                    {info.label}
                  </div>
                  <p className="text-sm text-slate-600">{info.description}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200">
          <div className="p-6 border-b border-slate-200">
            <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <Users className="w-5 h-5 text-teal-600" />
              Team Members ({users.length})
            </h2>
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <div className="w-12 h-12 border-4 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
              <p className="text-slate-600 mt-4">Loading users...</p>
            </div>
          ) : users.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">No users yet. Invite your first team member!</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left py-3 px-6 text-sm font-semibold text-slate-600">User</th>
                    <th className="text-left py-3 px-6 text-sm font-semibold text-slate-600">Email</th>
                    <th className="text-left py-3 px-6 text-sm font-semibold text-slate-600">Role</th>
                    <th className="text-left py-3 px-6 text-sm font-semibold text-slate-600">Status</th>
                    <th className="text-left py-3 px-6 text-sm font-semibold text-slate-600">Joined</th>
                    <th className="text-right py-3 px-6 text-sm font-semibold text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                            <span className="text-teal-700 font-semibold text-sm">
                              {(u.full_name || u.email).charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <span className="font-medium text-slate-900">{u.full_name || 'User'}</span>
                        </div>
                      </td>
                      <td className="py-4 px-6 text-slate-700">{u.email}</td>
                      <td className="py-4 px-6">
                        <select
                          value={u.role}
                          onChange={(e) => handleRoleChange(u.id, e.target.value)}
                          disabled={u.id === user?.id}
                          className={`px-3 py-1 rounded-full text-sm font-semibold border ${getRoleBadgeColor(u.role)} ${
                            u.id === user?.id ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                          }`}
                        >
                          <option value="super_admin">Super Admin</option>
                          <option value="admin">Admin</option>
                          <option value="manager">Manager</option>
                          <option value="staff">Staff</option>
                        </select>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex flex-col gap-1">
                          {u.approved ? (
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 inline-block w-fit">
                              ✓ Approved
                            </span>
                          ) : (
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800 inline-block w-fit">
                              ⏳ Pending
                            </span>
                          )}
                          {!u.active && (
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800 inline-block w-fit">
                              ✗ Inactive
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-6 text-slate-600 text-sm">
                        {new Date(u.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-4 px-6 text-right">
                        {confirmAction?.id === u.id ? (
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => {
                                if (confirmAction.action === 'approve') handleApproveUser(u.id, u.full_name || u.email);
                                else if (confirmAction.action === 'deactivate') handleDeactivateUser(u.id, u.full_name || u.email);
                                else if (confirmAction.action === 'remove') handleRemoveUser(u.id);
                              }}
                              className="px-3 py-1 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors"
                            >
                              Confirm
                            </button>
                            <button
                              onClick={() => setConfirmAction(null)}
                              className="px-3 py-1 bg-slate-100 text-slate-600 text-sm rounded-lg hover:bg-slate-200 transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div className="flex justify-end gap-2">
                            {!u.approved && (
                              <button
                                onClick={() => setConfirmAction({ id: u.id, action: 'approve' })}
                                className="px-3 py-1 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
                                title="Approve user"
                              >
                                Approve
                              </button>
                            )}
                            {u.approved && u.active && u.id !== user?.id && (
                              <button
                                onClick={() => setConfirmAction({ id: u.id, action: 'deactivate' })}
                                className="px-3 py-1 bg-yellow-600 text-white text-sm rounded-lg hover:bg-yellow-700 transition-colors"
                                title="Deactivate user"
                              >
                                Deactivate
                              </button>
                            )}
                            {!u.active && (
                              <button
                                onClick={() => setConfirmAction({ id: u.id, action: 'approve' })}
                                className="px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                                title="Reactivate user"
                              >
                                Reactivate
                              </button>
                            )}
                            {u.id !== user?.id && (
                              <button
                                onClick={() => setConfirmAction({ id: u.id, action: 'remove' })}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Remove user"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-6 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-2xl font-bold text-slate-900">Invite Team Member</h2>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Email Address</label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="colleague@example.com"
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Role</label>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                >
                  <option value="staff">Staff</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                </select>
                <p className="text-sm text-slate-600 mt-2">
                  {userService.getRoleInfo(inviteRole).description}
                </p>
              </div>
            </div>
            <div className="p-6 border-t border-slate-200 flex gap-3">
              <button
                onClick={() => setShowInviteModal(false)}
                className="flex-1 px-6 py-3 border border-slate-300 rounded-lg font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleInvite}
                className="flex-1 px-6 py-3 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors"
              >
                Send Invite
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
